import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from '@useHistory/useHistoryCompat';
import { Subject } from 'rxjs/internal/Subject';
import { distinctUntilChanged } from 'rxjs/operators';
import { HdWizard, HdWizardCurrentStep } from '../wizard-native/hd-wizard';
import { HdWizardController } from '../wizard-native/hd-wizard-controller';
import { HdWizardQueryParamHandler } from '../wizard-native/hd-wizard-query-param-handler';
import {
  HdWizardOptions,
  HdWizardStateWrapper,
  HdWizardStepTree,
  HdWizardStepTreeSnapshot
} from '../wizard-native/interface';
import { HdWizardStateMachineInterface } from '../wizard-native/state-machine';
import { HdWizardStepTracker } from '../wizard-native/step-tracker';
import { useWizardStorage } from './useWizardStorage';
import { useNavigationHistory } from '../../routing/contexts/NavigationHistoryContext';

export function useWizard({ skipInitialNavigation, onComplete }: { skipInitialNavigation?: boolean, onComplete?: VoidFunction }) {
  const history = useHistory();
  const storage = useWizardStorage();
  const { getStepsToGoBackToPath, cleanHistoryStack } = useNavigationHistory();

  const wizardRef = useRef<HdWizard>(null);
  const stepTrackerRef = useRef<HdWizardStepTracker>(null);
  const controllerRef = useRef<HdWizardController>(null);

  const [wizardCreated, setWizardCreated] = useState(false);

  const [wizard, setWizard] = useState<{
    currentStep?: HdWizardCurrentStep;
    wizardState?: HdWizardStateWrapper;
    stepTreeSnapshot?: HdWizardStepTreeSnapshot;
    stepGroups?: HdWizardStepTree;
  }>(null);

  const queryParamsHandler: HdWizardQueryParamHandler = useMemo(() => {
    return {
      getPath: (queryParamKey: string) => {
        return new URLSearchParams(history.location.search).get(queryParamKey);
      },
      updatePath: (queryParamKey: string, path: string, replaceUrl?: boolean) => {
        const urlSearchParams = new URLSearchParams(history.location.search);
        urlSearchParams.set(queryParamKey, path);

        const navigationPath = {
          search: urlSearchParams.toString()
        };

        if (replaceUrl) {
          history.replace(navigationPath);
        } else {
          history.push(navigationPath);
        }
      },
      changes: (queryParamKey, callback) => {
        const subject = new Subject();
        const pagePath = history.location.pathname;

        const setQueryParams = () => {
          const urlSearchParams = new URLSearchParams(history.location.search);
          subject.next(urlSearchParams.get(queryParamKey));
        };

        const historyListener = history.listen(() => {
          if (history.location.pathname !== pagePath) {
            return;
          }

          setQueryParams();
        });

        const subscription = subject.pipe(distinctUntilChanged()).subscribe(() => {
          callback();
        });

        if (!skipInitialNavigation) {
          setQueryParams();
        }

        return () => {
          historyListener();
          subscription.unsubscribe();
        };
      },
      goBack: (path?: string) => {
        if (path) {
          const stepsToGoBack = getStepsToGoBackToPath(path);
          const stepsToRemove = stepsToGoBack - 1;

          // Clean navigation history stack
          cleanHistoryStack(stepsToRemove);

          // Clean wizard step tracker - find the target step and remove everything after it
          if (stepTrackerRef.current) {
            const targetStepIndex = stepTrackerRef.current.getStepIndexFromPath(path);
            if (targetStepIndex >= 0) {
              const currentLength = stepTrackerRef.current.getLength();
              if (targetStepIndex + 1 < currentLength) {
                stepTrackerRef.current.splice(targetStepIndex + 1);
              }
            }
          }

          if (stepsToGoBack > 0) {
            history.go(-stepsToGoBack);
          } else {
            history.back();
          }
        } else {
          history.back();
        }
      }
    };
  }, [getStepsToGoBackToPath, cleanHistoryStack]);

  const createWizard = ({
    stepsHeader,
    options,
    stateMachine
  }: {
    stepsHeader?: HdWizardStepTree;
    options: HdWizardOptions;
    stateMachine: HdWizardStateMachineInterface;
  }) => {
    stepTrackerRef.current = new HdWizardStepTracker(stepsHeader, storage, options.trackingOptions);
    controllerRef.current = new HdWizardController(stepTrackerRef.current);
    wizardRef.current = new HdWizard(controllerRef.current, stateMachine, stepTrackerRef.current, queryParamsHandler);

    controllerRef.current.setWizardInstance(wizardRef.current);

    setWizardCreated(true);
  };

  useEffect(() => {
    if (!wizardCreated) {
      return;
    }

    const stateSub = controllerRef.current.wizardState$.subscribe(state => {
      setWizard(s => ({
        ...s,
        wizardState: state
      }));
    });

    const stepSub = controllerRef.current.currentStep$.subscribe(step => {
      setWizard(s => ({
        ...s,
        currentStep: step
      }));
    });

    const completeSub = controllerRef.current.complete.subscribe(() => {
      if (typeof onComplete === 'function') {
        onComplete();
      }
    });

    const snapshotSub = controllerRef.current
      .getStepTracker()
      .stepTreeSnapshot$.subscribe(snapshot => {
        setWizard(s => ({
          ...s,
          stepTreeSnapshot: snapshot
        }));
      });

    const stepGroupsSub = controllerRef.current
      .getStepTracker()
      .treeStepGroups$.subscribe(stepGroups => {
        setWizard(s => ({
          ...s,
          stepGroups
        }));
      });

    return () => {
      stateSub.unsubscribe();
      stepSub.unsubscribe();
      completeSub.unsubscribe();
      snapshotSub.unsubscribe();
      stepGroupsSub.unsubscribe();
      wizardRef.current.destroy();
    };
  }, [wizardCreated]);

  const selectStepGroup = (stepGroups: any, index: number) => {
    const step = controllerRef.current
      .getStepTracker()
      .getSteps()
      .find(visitedStep => visitedStep.path.startsWith(stepGroups.children[index].for));

    if (!step) {
      return;
    }

    controllerRef.current.selectStep(step.path);
  };

  return {
    wizardRef,
    stepTrackerRef,
    controllerRef,
    wizard: wizardRef.current,
    stepTracker: stepTrackerRef.current,
    controller: controllerRef.current,
    wizardState: wizard?.wizardState,
    currentStep: wizard?.currentStep,
    stepTreeSnapshot: wizard?.stepTreeSnapshot,
    stepGroups: wizard?.stepGroups,
    wizardCreated,
    selectStepGroup,
    createWizard
  };
}
